import * as React from 'react';

import { Col, IconButton, Row, TextField } from '@rematter/ui-web';
import { useAtom } from 'jotai';
import { LucidePlusCircle, Search } from 'lucide-react';

import {
  $linkedOrdersSearchTerm,
  $linkedOrdersSelectedId,
  $linkedOrdersSelectedType,
  LinkedOrderType,
} from '../../_state/linkedOrdersTab.state';
import { useTranslations } from '../../i18n/useTranslations';
import { OrderRecordForLink } from '../../types/brokeredOrder';

import { LinkOrderCard } from './LinkOrderCard';

import { useDebouncedValue } from '~/shared/hooks/useDebounce';

type LinkedOrderSectionProps = {
  title: string;
  emptyStateText: string;
  noResultsText: string;
  linkButtonAriaLabel: string;
  orders: OrderRecordForLink[];
  filteredOrders: OrderRecordForLink[];
  orderType: LinkedOrderType;
  selectedId: string | null;
  selectedType: LinkedOrderType | null;
  canEdit: boolean;
  onLinkOrder: () => void;
  onSelectOrder: (id: string, type: LinkedOrderType) => void;
};

const LinkedOrderSection: React.FC<LinkedOrderSectionProps> = ({
  title,
  emptyStateText,
  noResultsText,
  linkButtonAriaLabel,
  orders,
  filteredOrders,
  orderType,
  selectedId,
  selectedType,
  canEdit,
  onLinkOrder,
  onSelectOrder,
}) => {
  const hasOrders = orders.length > 0;
  const hasFilteredResults = filteredOrders.length > 0;

  return (
    <div className="@LinkedOrderSection">
      <Row xBetween yCenter className="mb-2">
        <Col>
          <h3 className="text-xs font-semibold text-neutral/75 uppercase">
            {title}
          </h3>
          {!hasOrders && (
            <p className="text-xs text-neutral/60">{emptyStateText}</p>
          )}
        </Col>

        <IconButton
          variant="solid"
          color="white"
          icon={<LucidePlusCircle className="size-4" />}
          onPress={onLinkOrder}
          isDisabled={!canEdit}
          aria-label={linkButtonAriaLabel}
        />
      </Row>

      {hasOrders && (
        <Col gap2>
          {hasFilteredResults ? (
            filteredOrders.map((order) => (
              <LinkOrderCard
                key={order.id}
                order={order}
                orderType={orderType}
                isSelected={
                  selectedId === order.id && selectedType === orderType
                }
                onClick={() => onSelectOrder(order.id, orderType)}
              />
            ))
          ) : (
            <p className="text-sm text-neutral/50">{noResultsText}</p>
          )}
        </Col>
      )}
    </div>
  );
};

const filterOrdersBySearchTerm = (
  orders: OrderRecordForLink[],
  searchTerm: string,
): OrderRecordForLink[] => {
  if (!searchTerm) return orders;

  const normalizedSearch = searchTerm.toLowerCase();

  return orders.filter(
    (order) =>
      order.documentNumber?.toLowerCase().includes(normalizedSearch) ||
      order.accountName?.toLowerCase().includes(normalizedSearch),
  );
};

type LinkedOrdersSidebarProps = {
  purchaseOrders: OrderRecordForLink[];
  salesOrders: OrderRecordForLink[];
  canEdit: boolean;
  onLinkOrder: (type: LinkedOrderType) => void;
};

export const LinkedOrdersSidebar: React.FC<LinkedOrdersSidebarProps> = ({
  purchaseOrders,
  salesOrders,
  canEdit,
  onLinkOrder,
}) => {
  const t = useTranslations();

  const [searchTerm, setSearchTerm] = useAtom($linkedOrdersSearchTerm);
  const [selectedId, setSelectedId] = useAtom($linkedOrdersSelectedId);
  const [selectedType, setSelectedType] = useAtom($linkedOrdersSelectedType);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const filteredPurchaseOrders = React.useMemo(
    () => filterOrdersBySearchTerm(purchaseOrders, debouncedSearchTerm),
    [purchaseOrders, debouncedSearchTerm],
  );

  const filteredSalesOrders = React.useMemo(
    () => filterOrdersBySearchTerm(salesOrders, debouncedSearchTerm),
    [salesOrders, debouncedSearchTerm],
  );

  React.useEffect(() => {
    if (!selectedId) {
      if (filteredPurchaseOrders.length > 0) {
        setSelectedId(filteredPurchaseOrders[0].id);
        setSelectedType('purchaseOrder');
      } else if (filteredSalesOrders.length > 0) {
        setSelectedId(filteredSalesOrders[0].id);
        setSelectedType('salesOrder');
      }
    }
  }, [
    filteredPurchaseOrders,
    filteredSalesOrders,
    selectedId,
    setSelectedId,
    setSelectedType,
  ]);

  const handleSelectOrder = React.useCallback(
    (id: string, type: LinkedOrderType) => {
      setSelectedId(id);
      setSelectedType(type);
    },
    [setSelectedId, setSelectedType],
  );

  return (
    <Col className="@LinkedOrdersSidebar h-full w-64 flex-shrink-0">
      <div className="flex-shrink-0 pb-2">
        <TextField
          placeholder={t.orders.detail.linkedOrders.searchPlaceholder}
          value={searchTerm}
          onChange={setSearchTerm}
          leftAdornment={<Search size={18} className="text-neutral/60" />}
          className="h-10 w-full rounded-lg border-neutral/10 bg-white"
        />
      </div>

      <Col gap4 className="min-h-0 flex-1 overflow-y-auto">
        <LinkedOrderSection
          title={t.orders.detail.linkedOrders.purchaseOrdersCount(
            filteredPurchaseOrders.length,
          )}
          emptyStateText={t.orders.detail.linkedOrders.noPurchaseOrders}
          noResultsText={t.orders.detail.linkedOrders.noPurchaseOrdersFound}
          linkButtonAriaLabel={t.orders.detail.linkedOrders.linkPurchaseOrder}
          orders={purchaseOrders}
          filteredOrders={filteredPurchaseOrders}
          orderType="purchaseOrder"
          selectedId={selectedId}
          selectedType={selectedType}
          canEdit={canEdit}
          onLinkOrder={() => onLinkOrder('purchaseOrder')}
          onSelectOrder={handleSelectOrder}
        />

        <LinkedOrderSection
          title={t.orders.detail.linkedOrders.salesOrdersCount(
            filteredSalesOrders.length,
          )}
          emptyStateText={t.orders.detail.linkedOrders.noSalesOrders}
          noResultsText={t.orders.detail.linkedOrders.noSalesOrdersFound}
          linkButtonAriaLabel={t.orders.detail.linkedOrders.linkSalesOrder}
          orders={salesOrders}
          filteredOrders={filteredSalesOrders}
          orderType="salesOrder"
          selectedId={selectedId}
          selectedType={selectedType}
          canEdit={canEdit}
          onLinkOrder={() => onLinkOrder('salesOrder')}
          onSelectOrder={handleSelectOrder}
        />
      </Col>
    </Col>
  );
};
